import { z } from "zod";

import { acceptLegalSchema } from "@medceptor/legal/schemas";

import {
  AUDIENCE_OPTIONS,
  CONCERN_OPTIONS,
  EXAM_TIMEFRAME_OPTIONS,
  REFERRAL_SOURCE_OPTIONS,
  TRACK_OPTIONS,
  WEEKLY_SCENARIO_GOAL_OPTIONS,
  type UserAudience,
  type UserTrack,
} from "./constants";

// Cast through the generated DB enum types so a future migration that
// extends `user_audience` / `user_track` widens the Zod union too —
// without this, the assertion would silently keep the old union and
// new values would fail validation at runtime.
const audienceValues = AUDIENCE_OPTIONS.map((o) => o.value) as [
  UserAudience,
  ...UserAudience[],
];

const trackValues = TRACK_OPTIONS.map((o) => o.value) as [
  UserTrack,
  ...UserTrack[],
];

const examTimeframeValues = EXAM_TIMEFRAME_OPTIONS.map((o) => o.value);
const concernValues = CONCERN_OPTIONS.map((o) => o.value);
const weeklyGoalValues: ReadonlyArray<number> = WEEKLY_SCENARIO_GOAL_OPTIONS;

// Form schema — used by both Conform's client-side validation AND
// the server action's parseWithZod call. Built by extending
// acceptLegalSchema with the onboarding-specific fields so a single
// submit captures both consent + onboarding data atomically; the
// cofounder chose this so we never collect personal info before the
// user has agreed to the privacy policy that governs it.
//
// The display-name single-column choice (vs first/last split) honors
// Layer 2a's resolved decision — see profiles.display_name comment in
// supabase/migrations/20260423003052_layer_2a_auth_and_orgs.sql.
// Custom error messages everywhere — Zod's defaults ("Invalid input:
// expected string, received undefined", "Invalid option: expected one
// of \"foo\"|\"bar\"") leak the schema shape to the user. Each field
// passes a user-friendly string at the type-constructor level so the
// missing-from-FormData case (undefined → fails type check before
// .min(1) ever runs) doesn't surface raw Zod text.
export const onboardingSchema = acceptLegalSchema.extend({
  display_name: z
    .string({ error: "Please enter your name." })
    .min(1, "Please enter your name.")
    .max(120, "Name is too long (max 120 characters)."),
  // Trim happens in the action (`v.phone?.trim() || undefined`), then
  // again in the RPC (`coalesce(nullif(trim(p_phone), ''), phone)`)
  // for defense-in-depth. Don't add `.trim()` here — Conform writes
  // the raw form value back on validation failure, and a Zod-side trim
  // would silently mutate what the user sees as their input.
  phone: z
    .string()
    .max(32, "Phone number is too long (max 32 characters).")
    .optional()
    .or(z.literal("")),
  organization: z
    .string()
    .max(200, "Organization name is too long (max 200 characters).")
    .optional()
    .or(z.literal("")),
  track: z.enum(trackValues, { error: "Please pick your track." }),
  audience: z.enum(audienceValues, {
    error: "Please tell us whether you're a student or instructor.",
  }),
  // Optional — attribution data is nice-to-have, not load-bearing.
  // `"Other"` is the sentinel for "don't know / not listed"; making
  // the whole field optional removes the pressure to pick *something*
  // when nothing fits. DB column is already nullable + the RPC param
  // accepts NULL.
  referral_source: z
    .enum(REFERRAL_SOURCE_OPTIONS)
    .optional()
    .or(z.literal("")),
  // Free-text shown only when referral_source = "Other". Optional —
  // user may pick "Other" without elaborating. Server-side
  // `encodeReferralSource` collapses to `"Other: <note>"` or `"Other"`.
  // Max 50 characters: the `onboarding_responses.referral_source`
  // CHECK constraint caps the encoded value at 64 chars (Layer 2c
  // migration). Encoded prefix `"Other: "` is 7 chars, so the note
  // must be ≤ 57. Round down to 50 to leave headroom + match a clean
  // user-visible cap.
  referral_source_other: z
    .string()
    .max(50, "Note is too long (max 50 characters).")
    .optional()
    .or(z.literal("")),
  // Guided-plan answers (migration 20261001060000). All optional: instructors
  // and written-exam-only tracks never see the screens, and a student whose
  // pre-sign-in draft was lost (email confirmed in another browser) resumes
  // at the required steps only. Values are the curated keys in constants.ts.
  exam_timeframe: z.enum(examTimeframeValues).optional().or(z.literal("")),
  // Conform submits repeated `concerns` inputs as an array, a single one as a
  // string; normalize both to an array.
  concerns: z
    .preprocess(
      (v) => (v === undefined || Array.isArray(v) ? v : [v]),
      z.array(z.enum(concernValues)).max(CONCERN_OPTIONS.length),
    )
    .optional(),
  weekly_scenario_goal: z.coerce
    .number()
    .refine((n) => weeklyGoalValues.includes(n), "Pick a weekly pace.")
    .optional()
    .or(z.literal("")),
});

export type OnboardingInput = z.infer<typeof onboardingSchema>;

// Settings → study-track switcher (a post-onboarding re-pick of the same
// dropdown). Derived, not re-declared, so the enum values and error copy
// have exactly one source.
export const trackSwitchSchema = onboardingSchema.pick({ track: true });

/** What the guided flow may hold BEFORE sign-in (localStorage). Derived from
 *  onboardingSchema so field rules have one owner, and deliberately PICKS only
 *  non-personal fields: name, phone and organization are asked after sign-in,
 *  next to the consent checkboxes, so nothing personal is stored before the
 *  privacy policy is accepted (the cofounder's rule above). The referral
 *  "Other" note is free text that may name a person, so it is asked on the
 *  About step too, never stored here. A key that isn't picked here cannot be
 *  written to the draft. */
export const onboardingDraftAnswersSchema = onboardingSchema
  .pick({
    audience: true,
    track: true,
    exam_timeframe: true,
    concerns: true,
    weekly_scenario_goal: true,
    referral_source: true,
  })
  .partial();
export type OnboardingDraftAnswers = z.infer<typeof onboardingDraftAnswersSchema>;
